import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useNotification } from "@/providers/NotificationProvider";
import { useAnalytics } from "@/providers/AnalyticsProvider";
import { notifications } from "@/strings";

/**
 * Hook to handle Google Classroom token expiration errors consistently.
 *
 * When a GC API call returns 401, this:
 * 1. Shows an error notification
 * 2. Invalidates the current-user query to update gcMode in UI
 * 3. Tracks the event for analytics
 *
 * @returns isGCTokenExpired - function to check if an error is a token expiration
 * @returns handleGCTokenExpired - function to handle token expiration
 */
export function useGCTokenExpiration() {
  const queryClient = useQueryClient();
  const { show } = useNotification();
  const { track } = useAnalytics();

  const isGCTokenExpired = useCallback((error: unknown): boolean => {
    if (error instanceof AxiosError) {
      return error.response?.status === 401;
    }
    return false;
  }, []);

  const handleGCTokenExpired = useCallback(
    (context: string) => {
      show(notifications.googleClassroom.tokenExpired, "error");
      track("gc_token_expired", { context });
      // Refetch GC status immediately so the banner shows right away
      void queryClient.refetchQueries({ queryKey: ["gc-status"] });
      // Also refetch user data to update gcMode in UI
      void queryClient.refetchQueries({ queryKey: ["current-user"] });
    },
    [show, track, queryClient]
  );

  return { isGCTokenExpired, handleGCTokenExpired };
}
